import { useState } from 'react';
import { Sheet } from '../design/atoms';
import Icon from '../design/Icon';
import {
  METRIC_KINDS, METRIC_ORDER, WORKOUT_TYPES, sentence,
  type MetricKind, type TargetMap, type WorkoutType,
} from './planShared';

/* SPEC 07/08 — one component, two schemas. The sheet renders whatever metric
   kinds the workout type declares; an unset metric stays OPEN (absent from the
   map), which is a deliberate, loggable-later state rather than a zero. */

interface Props {
  exerciseName: string;
  workoutType: WorkoutType;
  initial: TargetMap;
  mode: 'target' | 'actual';
  saving?: boolean;
  onSave: (map: TargetMap) => void;
  onClose: () => void;
}

/** Steps of 2.5/0.5 otherwise accumulate float dust across taps. */
const clean = (v: number) => Number(v.toFixed(2));

export default function TargetSheet({ exerciseName, workoutType, initial, mode, saving, onSave, onClose }: Props) {
  const [map, setMap] = useState<TargetMap>({ ...initial });

  const kinds = (WORKOUT_TYPES[workoutType]?.metricKeys ?? []).slice()
    .sort((a, b) => METRIC_ORDER.indexOf(a) - METRIC_ORDER.indexOf(b));

  const setKind = (k: MetricKind, value: number | null) =>
    setMap(prev => {
      const next = { ...prev };
      if (value === null) delete next[k];
      else next[k] = clean(value);
      return next;
    });

  const bump = (k: MetricKind, dir: 1 | -1) => {
    const def = METRIC_KINDS[k];
    const current = map[k];
    if (current == null) {
      // ⊕ on an off tile turns it on at the default; ⊖ leaves it off.
      if (dir === 1) setKind(k, def.default);
      return;
    }
    const next = current + dir * def.step;
    setKind(k, next < def.step ? null : next);
  };

  return (
    <Sheet onClose={onClose}>
      <div className="col" style={{ gap: 2 }}>
        <div className="lbl">{mode === 'actual' ? 'Log' : 'Target'} · {WORKOUT_TYPES[workoutType]?.label ?? workoutType}</div>
        <div className="dp dp-m trunc">{exerciseName}</div>
      </div>

      {/* Live read-back — this is what teaches the sparse-target model */}
      <div className="card ink" style={{ padding: 14 }}>
        <div className="sub wrap" style={{ color: 'var(--on-ink)', fontSize: 14 }}>
          {sentence(exerciseName, map)}
        </div>
      </div>

      <div className="grid2">
        {kinds.map(k => {
          const def = METRIC_KINDS[k];
          const on = map[k] != null;
          return (
            <div
              key={k}
              className="card"
              style={{
                padding: 12,
                boxShadow: 'none',
                background: on ? 'var(--tint2)' : 'transparent',
                border: on ? '1px solid var(--gold-line)' : '1.5px dashed var(--line-2)',
                opacity: on ? 1 : 0.62,
              }}
            >
              <div className="col" style={{ gap: 8 }}>
                <div className="between">
                  <span className="lbl">{def.label}</span>
                  <button
                    aria-label={on ? `Turn off ${def.label}` : `Turn on ${def.label}`}
                    onClick={() => setKind(k, on ? null : def.default)}
                    style={{
                      width: 20, height: 20, borderRadius: 6, display: 'grid', placeItems: 'center',
                      border: `1.5px solid ${on ? 'var(--gold-2)' : 'var(--line-2)'}`,
                      background: on ? 'var(--gold-soft)' : 'transparent',
                    }}
                  >
                    {on && <Icon n="check" s={13} c="var(--gold-2)" />}
                  </button>
                </div>
                <div className="row" style={{ alignItems: 'baseline', gap: 5 }}>
                  <span className="num" style={{ fontSize: 22 }}>{on ? map[k] : '—'}</span>
                  <span className="tiny">{def.unit}</span>
                </div>
                <div className="step" style={{ alignSelf: 'flex-start' }}>
                  <button aria-label={`decrease ${def.label}`} onClick={() => bump(k, -1)}>−</button>
                  <b>{on ? map[k] : '—'}</b>
                  <button aria-label={`increase ${def.label}`} onClick={() => bump(k, 1)}>+</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="tiny wrap">
        Turn off what you don't want to commit to — an unset metric stays open and you can still log it later.
      </div>

      <button className="btn gold block" disabled={saving} onClick={() => onSave(map)}>
        {saving ? 'Saving…' : mode === 'actual' ? 'Log this' : 'Set target'}
      </button>
    </Sheet>
  );
}
